import { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { applyHeroOverrides } from "./components/PageHero";
import Login from "./pages/Login";
import PublicHome from "./pages/PublicHome";
import Dashboard from "./pages/Dashboard";
import ChiSiamo from "./pages/ChiSiamo";
import Servizi from "./pages/Servizi";
import ServiziCategoria from "./pages/ServiziCategoria";
import SubCategoriaVideos from "./pages/SubCategoriaVideos";
import Strumenti from "./pages/Strumenti";
import CalcolatoreRischio from "./pages/CalcolatoreRischio";
import InteresseComposto from "./pages/InteresseComposto";
import CalendarioEconomico from "./pages/CalendarioEconomico";
import StrumentiSubcategoria from "./pages/StrumentiSubcategoria";
import LinkUtili from "./pages/LinkUtili";
import DiventaStudente from "./pages/DiventaStudente";
import FormStudenti from "./pages/FormStudenti";
import TradingJournal from "./pages/TradingJournal";
import AnalisiStudenti from "./pages/AnalisiStudenti";
import Shop from "./pages/Shop";
import AdminShop from "./pages/AdminShop";
import Impostazioni from "./pages/Impostazioni";
import Header from "./components/Header";
import PublicHeader from "./components/PublicHeader";
import ThemeToggle from "./components/ThemeToggle";
import Footer from "./components/Footer";
import ScrollReveal from "./components/ScrollReveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);
export const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

const setAxiosToken = (token) => {
  if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  else delete axios.defaults.headers.common["Authorization"];
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full" style={{ borderColor: "var(--mm-gold)", borderTopColor: "transparent" }} />
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, isAdmin } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState("dark");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [, setHeroVersion] = useState(0);

  useEffect(() => {
    const storedTheme = localStorage.getItem("mm_theme") || "dark";
    setTheme(storedTheme);
    document.body.classList.toggle("light-theme", storedTheme === "light");

    const token = localStorage.getItem("mm_token");
    if (token) {
      setAxiosToken(token);
      axios.get(`${API}/auth/me`)
        .then(res => setUser(res.data))
        .catch(() => {
          localStorage.removeItem("mm_token");
          setAxiosToken(null);
          setUser(false);
        });
    } else {
      setUser(false);
    }

    axios.post(`${API}/seed`).catch(() => {});

    axios.get(`${API}/settings/hero_images`).then(r => {
      try {
        const overrides = JSON.parse(r.data.value);
        if (overrides && Object.keys(overrides).length) {
          applyHeroOverrides(overrides);
          setHeroVersion(v => v + 1);
        }
      } catch {}
    }).catch(() => {});
  }, []);

  const login = async (password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { password });
      localStorage.setItem("mm_token", res.data.token);
      setAxiosToken(res.data.token);
      setUser(res.data.user);
      return { success: true };
    } catch (e) {
      return { success: false, message: formatApiErrorDetail(e.response?.data?.detail) };
    }
  };

  const logout = () => {
    localStorage.removeItem("mm_token");
    setAxiosToken(null);
    setUser(false);
    sessionStorage.clear();
  };

  const toggleTheme = () => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    localStorage.setItem("mm_theme", newTheme);
    document.body.classList.toggle("light-theme", newTheme === "light");
  };

  const isAuthenticated = !!user;
  const isAdmin = user && user.role === "admin";
  const userRole = user ? user.role : null;

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, userRole, isAdmin, login, logout }}>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <div className={`App min-h-screen ${theme === "light" ? "light-theme" : ""}`}>
          <BrowserRouter>
            {user ? (
              <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
            ) : user === false ? (
              <PublicHeader />
            ) : null}
            <Routes>
              <Route path="/" element={
                user === null ? <Spinner /> : user ? <Navigate to="/dashboard" replace /> : <PublicHome />
              } />
              <Route path="/login" element={
                user ? <Navigate to="/dashboard" replace /> : <Login />
              } />
              <Route path="/diventa-studente" element={<DiventaStudente />} />
              <Route path="/presentazione" element={<Navigate to="/diventa-studente" replace />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/chi-siamo" element={<ProtectedRoute><ChiSiamo /></ProtectedRoute>} />
              <Route path="/servizi" element={<ProtectedRoute><Servizi /></ProtectedRoute>} />
              <Route path="/servizi/:slug/:subslug" element={<ProtectedRoute><SubCategoriaVideos /></ProtectedRoute>} />
              <Route path="/servizi/:slug" element={<ProtectedRoute><ServiziCategoria /></ProtectedRoute>} />
              <Route path="/strumenti" element={<ProtectedRoute><Strumenti /></ProtectedRoute>} />
              <Route path="/strumenti/calcolatore-rischio" element={<ProtectedRoute><CalcolatoreRischio /></ProtectedRoute>} />
              <Route path="/strumenti/interesse-composto" element={<ProtectedRoute><InteresseComposto /></ProtectedRoute>} />
              <Route path="/strumenti/calendario-economico" element={<ProtectedRoute><CalendarioEconomico /></ProtectedRoute>} />
              <Route path="/strumenti/trading-journal" element={<ProtectedRoute><TradingJournal /></ProtectedRoute>} />
              <Route path="/admin/analisi-studenti" element={<AdminRoute><AnalisiStudenti /></AdminRoute>} />
              <Route path="/shop" element={<ProtectedRoute><Shop /></ProtectedRoute>} />
              <Route path="/admin/shop" element={<AdminRoute><AdminShop /></AdminRoute>} />
              <Route path="/strumenti/:slug" element={<ProtectedRoute><StrumentiSubcategoria /></ProtectedRoute>} />
              <Route path="/link-utili" element={<ProtectedRoute><LinkUtili /></ProtectedRoute>} />
              <Route path="/impostazioni" element={<AdminRoute><Impostazioni /></AdminRoute>} />
              <Route path="/admin/form-studenti" element={<AdminRoute><FormStudenti /></AdminRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            {user !== null && <Footer />}
            <ScrollReveal />
            {isAuthenticated && <ThemeToggle />}
          </BrowserRouter>
          <Toaster position="top-right" />
        </div>
      </ThemeContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
